import {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { Search, Users } from "lucide-react";
import { ApiContext } from "../../../context/MyContext";
import Loader from "../../../common/Loader/Loader";
import { Toaster } from "../../../common/Toaster/Toaster";
import VerifyEmpModal from "./VerifyEmpModal/VerifyEmpModal";
import UpdateRoleModal from "./UpdateRoleModal/UpdateRoleModal";
import AssignManagerModal from "./AssignManagerModal/AssignManagerModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal/DeleteConfirmationModal";
import { EmployeeDrawer } from "./EmployeeDrawer";
import { EmployeeAvatar } from "./EmployeeAvatar";
import {
  fetchEmployees,
  fetchDepartments,
} from "../../../service/employeeService";
import {
  hasDashboardAccess,
  hasButtonPermission,
} from "../../../utils/accessControl";
import {
  filterEmployees,
  formatEmployeeList,
  personKey,
} from "./employeeListUtils";

const DASHBOARD_ID = "emp-dashboard";

const TABS = [
  { id: "active",  label: "Active" },
  { id: "pending", label: "Pending Verification" },
  { id: "no-role", label: "No Active Role" },
];



function EmployeeCard({ emp, onCardClick }) {
  const primaryRole = emp.current_roles[0];

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`View details for ${emp.name}`}
      className="flex items-center gap-4 px-4 sm:px-6 py-4 hover:bg-[#FFF3EB] transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-orange-primary"
      onClick={() => onCardClick(emp)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onCardClick(emp); }}
    >
      <EmployeeAvatar name={emp.name} />

      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-900 text-sm truncate">{emp.name}</p>
        <p className="text-xs text-gray-500 truncate mt-0.5">{emp.email || "—"}</p>
      </div>

      <div className="hidden sm:block w-36 flex-shrink-0 text-sm text-gray-700 truncate">
        {primaryRole?.department_title || <span className="text-gray-300">—</span>}
      </div>

      <div className="hidden sm:block w-36 flex-shrink-0 text-sm text-gray-700 truncate">
        {primaryRole?.role_title || <span className="text-gray-300">—</span>}
      </div>

      <div className="hidden sm:block w-32 flex-shrink-0 text-sm text-gray-500 truncate">
        {(primaryRole?.manager_name && primaryRole.manager_name !== "N/A")
          ? primaryRole.manager_name
          : <span className="text-gray-300">—</span>}
      </div>

      <div className="hidden lg:block w-28 flex-shrink-0 text-xs text-gray-500">
        {emp.joined_at
          ? new Date(emp.joined_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
          : <span className="text-gray-300">—</span>}
      </div>
    </div>
  );
}

export const EmpDashboard = () => {
  const { get, employeeData, departmentsWithRoles } = useContext(ApiContext);

  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("active");
  const [drawerEmployee, setDrawerEmployee] = useState(null);
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState(null);

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
  }, []);

  const canAccess = hasDashboardAccess(DASHBOARD_ID, employeeData, departmentsWithRoles);
  const canVerify = hasButtonPermission("verify-employee", DASHBOARD_ID, employeeData, departmentsWithRoles);
  const canUpdateRole = hasButtonPermission("update-role", DASHBOARD_ID, employeeData, departmentsWithRoles);
  const canAssignManager = hasButtonPermission("assign-manager", DASHBOARD_ID, employeeData, departmentsWithRoles);
  const canDelete = hasButtonPermission("delete-employee", DASHBOARD_ID, employeeData, departmentsWithRoles);

  const filteredEmployees = useMemo(
    () => filterEmployees(employees, searchQuery),
    [employees, searchQuery]
  );

  const activeCount  = useMemo(() => filteredEmployees.filter(e => e.is_verified && e.current_roles.length > 0).length, [filteredEmployees]);
  const pendingCount = useMemo(() => filteredEmployees.filter(e => !e.is_verified).length, [filteredEmployees]);
  const noRoleCount  = useMemo(() => filteredEmployees.filter(e => e.is_verified && e.current_roles.length === 0).length, [filteredEmployees]);

  const tabEmployees = useMemo(() => {
    if (activeTab === "active")  return filteredEmployees.filter(e => e.is_verified && e.current_roles.length > 0);
    if (activeTab === "pending") return filteredEmployees.filter(e => !e.is_verified);
    return filteredEmployees.filter(e => e.is_verified && e.current_roles.length === 0);
  }, [filteredEmployees, activeTab]);

  const tabCount = { active: activeCount, pending: pendingCount, "no-role": noRoleCount };

  const refreshEmployees = useCallback(async (deptList = departments) => {
    try {
      const { employees: apiEmployees } = await fetchEmployees(get);
      const formatted = formatEmployeeList(apiEmployees, deptList);
      setEmployees(formatted);
      // Keep the drawer in sync — find the updated employee object.
      setDrawerEmployee((prev) => {
        if (!prev) return null;
        const key = personKey(prev);
        return formatted.find((e) => personKey(e) === key) ?? null;
      });
    } catch (err) {
      console.error("Error fetching employee data:", err.message);
      showToast("Error fetching employees", "error");
    }
  }, [departments, get, showToast]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      let deptData = [];
      try {
        deptData = await fetchDepartments(get);
        if (!cancelled) setDepartments(deptData);
      } catch (err) {
        console.error("Error fetching departments:", err.message);
      }
      try {
        const { employees: apiEmployees } = await fetchEmployees(get);
        if (!cancelled) setEmployees(formatEmployeeList(apiEmployees, deptData));
      } catch (err) {
        console.error("Error fetching employee data:", err.message);
        if (!cancelled) { showToast("Error fetching employees", "error"); setEmployees([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [get, showToast]);

  const closeModal = useCallback(() => setModal(null), []);
  const openVerify  = useCallback((employee) => setModal({ type: "verify",  employee }), []);
  const openRoles   = useCallback((employee) => setModal({ type: "roles",   employee }), []);
  const openDelete  = useCallback((employee) => setModal({ type: "delete",  employee }), []);
  const openManager = useCallback((employee, role) => {
    setModal({
      type: "manager",
      row: {
        ...employee,
        role: role.role_title,
        department: role.department_title,
        manager_name: role.manager_name,
        employee_role_id: role.employee_role_id,
      },
    });
  }, []);

  const onModalSuccess = useCallback(async () => {
    closeModal();
    await refreshEmployees();
  }, [closeModal, refreshEmployees]);

  const isSearching = Boolean(searchQuery.trim());

  const emptyMessage = {
    active:   { primary: "No active employees", secondary: "Active employees have been verified and hold at least one current role." },
    pending:  { primary: "No employees awaiting verification", secondary: "New staff appear here after accepting terms of service." },
    "no-role":{ primary: "No employees without an active role", secondary: "Verified employees with all roles expired appear here." },
  }[activeTab];

  return (
    <div className="flex-1 flex flex-col min-h-0 w-full">
      {loading ? (
        <Loader />
      ) : (
        <div className="w-full px-4 sm:px-6 py-6 overflow-auto">
          {/* Page heading */}
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Employees</h1>
            <p className="mt-1 text-sm text-gray-500">
              Your organisation's staff — their roles, departments, and reporting lines.
            </p>
          </div>

          {!canAccess ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <p className="text-gray-600">You don&apos;t have permission to view this dashboard.</p>
            </div>
          ) : (
            <>
              {/* Search bar */}
              <div className="relative max-w-md mb-5">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search by name, email, department, role or manager…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-primary/50 focus:border-orange-primary transition-shadow"
                />
              </div>

              {/* Tabs */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {TABS.map((tab) => {
                  const count = tabCount[tab.id];
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-orange-primary/50
                        ${isActive
                          ? "bg-orange-primary text-white shadow-sm"
                          : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                    >
                      {tab.label}
                      <span className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full text-[11px] font-semibold
                        ${isActive ? "bg-white/25 text-white" : "bg-gray-100 text-gray-600"}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Employee list */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Column header — hidden on mobile */}
                <div className="hidden sm:flex items-center gap-4 px-4 sm:px-6 py-2 bg-light-orange border-b border-gray-200">
                  <div className="w-9 flex-shrink-0" />
                  <div className="flex-1 text-xs font-semibold uppercase tracking-wider text-gray-500">Employee</div>
                  <div className="w-36 flex-shrink-0 text-xs font-semibold uppercase tracking-wider text-gray-500">Department</div>
                  <div className="w-36 flex-shrink-0 text-xs font-semibold uppercase tracking-wider text-gray-500">Role</div>
                  <div className="w-32 flex-shrink-0 text-xs font-semibold uppercase tracking-wider text-gray-500">Manager</div>
                  <div className="hidden lg:block w-28 flex-shrink-0 text-xs font-semibold uppercase tracking-wider text-gray-500">Joined</div>
                </div>

                {tabEmployees.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 px-4">
                    <div className="rounded-full bg-gray-100 p-4 mb-4">
                      <Users className="w-10 h-10 text-gray-400" />
                    </div>
                    <p className="text-base font-medium text-gray-700">{emptyMessage.primary}</p>
                    <p className="text-sm text-gray-500 mt-1 max-w-sm text-center">{emptyMessage.secondary}</p>
                    {isSearching && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery("")}
                        className="mt-4 text-sm font-medium text-orange-primary hover:underline"
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {tabEmployees.map((emp) => (
                      <EmployeeCard
                        key={personKey(emp)}
                        emp={emp}
                        onCardClick={setDrawerEmployee}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Employee detail drawer */}
      <EmployeeDrawer
        employee={drawerEmployee}
        onClose={() => setDrawerEmployee(null)}
        canVerify={canVerify}
        canUpdateRole={canUpdateRole}
        canAssignManager={canAssignManager}
        canDelete={canDelete}
        onVerify={openVerify}
        onManageRoles={openRoles}
        onAssignManager={openManager}
        onDelete={openDelete}
      />

      {modal?.type === "verify" && modal.employee && (
        <VerifyEmpModal
          employee={modal.employee}
          departments={departments}
          onClose={closeModal}
          onVerifySuccess={onModalSuccess}
          onToast={showToast}
        />
      )}

      {modal?.type === "roles" && modal.employee && (
        <UpdateRoleModal
          employee={modal.employee}
          departments={departments}
          onClose={closeModal}
          onUpdateSuccess={onModalSuccess}
          onToast={showToast}
        />
      )}

      {modal?.type === "manager" && modal.row && (
        <AssignManagerModal
          rowData={modal.row}
          onClose={closeModal}
          onSuccess={onModalSuccess}
          onToast={showToast}
        />
      )}

      {modal?.type === "delete" && modal.employee && (
        <DeleteConfirmationModal
          employee={modal.employee}
          onClose={closeModal}
          onDeleteSuccess={onModalSuccess}
          onToast={showToast}
        />
      )}

      {toast?.message && <Toaster message={toast.message} type={toast.type} />}
    </div>
  );
};
